/* global dataLayer */

/* eslint-disable import/first */
import React, { useEffect } from 'react';
import NoSSR from 'react-no-ssr';
import Metrics from '@spotahome/soyuz-tracking';

import {
  ThemeProvider,
  CookiesProvider,
  ToastContainer,
  GoogleOneTap,
  SpottyCat,
  UserInfoProvider
} from '@spotahome/ui-library';
import {
  useSoyuzData,
  getPagePath,
  useSoyuzEnv,
  useSoyuzExperiment
} from '@spotahome/soyuz/client';

import getGqlClient from '@spotahome/landings-common/src/graphql/client';

import { getAllCities } from '@spotahome/landings-common/src/graphql/queries/cities';
import { sendTenantActionEvent } from '@spotahome/landings-common/src/graphql/mutations/tenantActions';

import {
  InstanceManager,
  CustomDimensionsManager,
  constants
} from '@spotahome/landings-common/src/tracking';

import AppBanner from '@spotahome/landings-common/src/components/AppBanner';
import PropertyTypeSection from '@spotahome/landings-common/src/components/PropertyTypeSection';
import OptInWrapper from '@spotahome/landings-common/src/components/OptInWrapper';
import CitiesShowcase from '@spotahome/landings-common/src/components/CitiesShowcase';
import Footer from '@spotahome/landings-common/src/components/Footer';
import LoginSignupModal from '@spotahome/landings-common/src/components/LoginSignupModal';

import FAQSection from '@spotahome/landings-common/src/components/FAQSection';

import {
  getTenantFaq,
  getTenantStructuredFaq
} from '@spotahome/landings-common/src/utils/faq';

import Hero from './components/Hero';
import FeatureBoxSection from './components/FeatureBoxSection';
import QuoteSection from './components/QuoteSection';
import GuaranteeSection from './components/GuaranteeSection';
import CardTestimonialQuoteSection from './components/CardTestimonialQuoteSection';
import HomeFooter from './components/HomeFooter';

import '@spotahome/landings-common/src/scss/init.scss';

const PRELOAD_FONTS = [
  'https://static.spotahome.com/fonts/Gotham-Bold.woff',
  'https://static.spotahome.com/fonts/Gotham-Book.woff',
  'https://static.spotahome.com/fonts/icons/spotahome-family-Regular.woff'
];

// @ TO-DO: Add the Agrandir Font to Amazon S3/CDN and preload it if we are under the experiment variant
// const PRELOAD_EXPERIMENTAL_FONT = [''];

const getFontPreload = () =>
  PRELOAD_FONTS.map(font => ({
    rel: 'preload',
    href: font,
    as: 'font',
    crossOrigin: 'anonymous'
  }));

const getTenantStructuredFaqScript = trans => ({
  type: 'application/ld+json',
  innerHTML: JSON.stringify(getTenantStructuredFaq(trans))
});

const LandingPage = () => {
  const AudiencesExperimentTest = useSoyuzExperiment('AudiencesExperimentTest');
  const { [LandingPage.NAME]: requestedData } = useSoyuzData();

  const { cityList, userId, initialCookie, deviceType, isSafari } =
    requestedData;

  const env = useSoyuzEnv();

  const isPartnerDeploy = env.SOYUZ_PUBLIC_BRAND.startsWith('partner_');

  // Hit in server rendering
  AudiencesExperimentTest.get('behaviour');

  useEffect(() => {
    Metrics.actions.configureClient(async ({ actionId, payload }) => {
      try {
        await sendTenantActionEvent(getGqlClient())({
          actionId,
          payload
        });
      } catch (e) {
        console.log('error sending sendTenantActionEvent:', e);
      }
    });
  }, []);

  useEffect(() => {
    if (userId && typeof dataLayer !== 'undefined') {
      dataLayer.push({ userId });
    }
  }, [userId]);

  return (
    <CookiesProvider initialCookie={initialCookie}>
      <ThemeProvider flags={{ newButtons: true }}>
        <UserInfoProvider isUserLogged={!!userId}>
          {!isPartnerDeploy ? <SpottyCat cities={cityList} /> : null}
          <AppBanner deviceType={deviceType} />
          <ToastContainer />
          {!userId && <GoogleOneTap />}
          <div data-browser={isSafari ? 'safari' : 'other'}>
            <Hero cityList={cityList} deviceType={deviceType} />
            <FeatureBoxSection />
            <QuoteSection />
            {isPartnerDeploy ? null : (
              <>
                <GuaranteeSection />
                <PropertyTypeSection deviceType={deviceType} />
                <CitiesShowcase cityList={cityList} deviceType={deviceType} />
                <NoSSR>
                  <CardTestimonialQuoteSection />
                  <FAQSection questions={getTenantFaq()} />
                  <HomeFooter />
                  <Footer />
                  <OptInWrapper />
                </NoSSR>
              </>
            )}
            <NoSSR>
              <LoginSignupModal />
            </NoSSR>
          </div>
        </UserInfoProvider>
      </ThemeProvider>
    </CookiesProvider>
  );
};

LandingPage.requestAllData = async (req, _, { res, graphqlClient }) => {
  const isPartnerDemoDeploy = process.env.SOYUZ_PUBLIC_BRAND === 'partner_demo';
  // This behavior is only for the partner white-label demonstration
  // We may want to change this in the future
  if (isPartnerDemoDeploy) {
    return res.redirect(
      302,
      getPagePath('marketplace.free-search', {
        params: {
          locationString: 'madrid'
        }
      })
    );
  }

  try {
    const citiesResponse = await getAllCities(graphqlClient)(req.locale);
    const { cities } = citiesResponse.data;
    const { locale, userId } = req;
    const serverTrackingInstance = InstanceManager.getServerSideInstance();
    const cdManager = CustomDimensionsManager(serverTrackingInstance);
    cdManager.setCommonCustomDimensions({
      userId,
      locale,
      pageType: constants.TRACKING_PAGE_TYPES.HOMEPAGE
    });
    serverTrackingInstance.sendEvent({
      eventCategory: 'Home',
      eventAction: 'initial load',
      eventLabel: '',
      eventNonInteraction: true
    });
    const dataLayerScript = serverTrackingInstance.flush();

    const isSafari =
      req.device.info && req.device.info.client
        ? req.device.info.client.name.toLowerCase().includes('safari')
        : false;

    return {
      headers: {
        scripts: [
          { innerHTML: dataLayerScript },
          getTenantStructuredFaqScript(req.trans)
        ],
        links: [getFontPreload()]
      },
      data: {
        cityList: cities.sort((a, b) => (a.id > b.id ? 1 : -1)),
        userId: req.userId,
        deviceType: req.device.type,
        initialCookie: req.headers.cookie,
        isSafari
      }
    };
  } catch (err) {
    req.logger.error('Landing page error', { err });
  }

  return {
    headers: {
      links: [getFontPreload()]
    },
    data: {
      cityList: [],
      userId: null
    }
  };
};

LandingPage.NAME = 'LandingPage';

export default LandingPage;
